import React, { ReactElement, useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Brand } from '../layout/Brand';
import { Row, Col, Button, Divider, message as Message } from 'antd';
import { FaFacebookF, FaGoogle } from 'react-icons/fa';
import { History } from 'history';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import {
  AuthenticationRouteProps,
  AuthenticationParams,
} from './Authentication';
import { facebookLogin, googleLogin } from '../../store/auth/actions';
import { useDispatch, useSelector } from 'react-redux';
import { AccountType } from '../../store/auth/types';
import { getAppGlobalState } from '../../store/app-globals/selectors';
import { setError } from '../../store/app-globals/actions';
import { getUserState } from '../../store/user/selector';
import { logoutuser } from '../../store/user/actions';

export interface LoginScreenState {
  forWho: string;
  link: string;
  linkLabel: string;
}

const getStaticScreenValues = (
  accountType: string | undefined
): LoginScreenState => ({
  forWho: accountType === 'guest' ? i18n.t('Guests') : i18n.t('Cooks'),
  link:
    accountType === 'guest'
      ? '/authentication/login/index/cook'
      : '/authentication/login/index/guest',
  linkLabel:
    accountType === 'guest'
      ? i18n.t('Became a Cook')
      : i18n.t('Became a Guest'),
});

export const Login: React.FC<AuthenticationRouteProps> = (): ReactElement => {
  const { accountType } = useParams<AuthenticationParams>();
  const history: History = useHistory();
  const [loginState, setLoginState] = useState<LoginScreenState>(
    getStaticScreenValues(accountType)
  );
  const { isLoading, hasError, errorMessage } = useSelector(getAppGlobalState);
  const { user } = useSelector(getUserState);
  const dispatch = useDispatch();
  const { forWho, link, linkLabel } = loginState;
  const _accountType: AccountType =
    accountType === 'cook' || accountType === 'guest' ? accountType : 'guest';
  const handleLinkClick = (link: string) => (): void => {
    history.push(link);
  };

  const handleFacebookLogin = () => {
    dispatch(facebookLogin(_accountType));
  };

  const handleGoogleLogin = () => {
    dispatch(googleLogin(_accountType));
  };

  const checkAuth = () => {
    if (user.userid) {
      if (user.status === 'suspended') {
        dispatch(logoutuser());
        Message.error(
          'Oops! this account is temporarily suspended, Please contact the admin for this concern'
        );
        return;
      }
      user.authComplete
        ? user.accountType === 'admin'
          ? history.push('/admin')
          : history.push('/profile')
        : history.push('/profile/create');
      return;
    }
  };

  // const isSuccess = () => {
  //   if (!isLoading && message) {
  //     Message.success(message);
  //     dispatch(setLoading(false, ''));
  //     history.push(`/profile/create`);
  //   }
  // };

  const isError = () => {
    if (hasError) {
      Message.error(errorMessage);
      dispatch(setError(false, ''));
      return;
    }
  };

  const { t } = useTranslation();
  useEffect(() => {
    setLoginState(getStaticScreenValues(accountType));

    // * cleanup
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return () => {};
  }, [accountType]);
  useEffect(checkAuth, [isLoading, user]);
  // useEffect(isSuccess, [isLoading, message]);
  useEffect(isError, [hasError, errorMessage]);

  return (
    <Row className="login" justify="center">
      <Brand />
      <Col span={24} className="for-who" flex={1}>
        {forWho}
      </Col>
      <Col span={24} className="login-button-container">
        <Button
          shape="round"
          className="login-button login-button-primary"
          onClick={handleLinkClick(
            `/authentication/registration/${accountType}`
          )}
        >
          {t('CREATE_AN_ACCOUNT')}
        </Button>
      </Col>
      <Divider className="login-separator">{t('OR_SIGN_IN')}</Divider>
      <Col span={24} className="login-button-container">
        <Button
          shape="round"
          className="login-button login-button-primary"
          onClick={handleLinkClick(`/authentication/login/email/:accountType`)}
        >
          {t('EMAIL_LOGIN')}
        </Button>
      </Col>
      <Col span={24} className="login-button-container">
        <Button
          shape="round"
          className="login-button login-button-facebook"
          icon={<FaFacebookF color="#FFF" />}
          onClick={handleFacebookLogin}
          loading={isLoading}
        >
          {t('FACEBOOK_LOGIN')}
        </Button>
      </Col>
      <Col span={24} className="login-button-container">
        <Button
          shape="round"
          className="login-button login-button-google"
          icon={<FaGoogle color="#FFF" />}
          onClick={handleGoogleLogin}
        >
          {t('GOOGLE_LOGIN')}
        </Button>
      </Col>
      <Col flex="1 0 100px" className="bottom-link-forgot">
        <Button type="link" onClick={handleLinkClick(link)}>
          {linkLabel}
        </Button>
      </Col>
    </Row>
  );
};
